import {create} from 'zustand'
import {persist} from 'zustand/middleware'
import {IUserPreferences} from '../types/auth'

type SidebarView = 'legacy' | 'new'

export type Lang = 'en' | 'es'

type State = {
  sidebarView: SidebarView
  sidebarFavorites: string[]
  expandedSections: string[]
  language: Lang
}

type Actions = {
  setSidebarView: (view: SidebarView) => void
  setFavorites: (favorites: string[]) => void
  toggleFavorite: (route: string) => void
  toggleSection: (sectionId: string) => void
  setLanguage: (lang: Lang) => void
  hydrate: (prefs: IUserPreferences) => void
  reset: () => void
}

export const useUserPreferencesStore = create<State & Actions>()(
  persist(
    (set) => ({
      sidebarView: 'new',
      sidebarFavorites: [],
      expandedSections: [],
      language: 'en',
      setSidebarView: (view) => set({sidebarView: view}),
      setLanguage: (lang) => set({language: lang}),
      setFavorites: (favorites) => set({sidebarFavorites: favorites}),
      toggleFavorite: (route) =>
        set((state) => ({
          sidebarFavorites: state.sidebarFavorites.includes(route)
            ? state.sidebarFavorites.filter((r) => r !== route)
            : [...state.sidebarFavorites, route],
        })),
      toggleSection: (sectionId) =>
        set((state) => ({
          expandedSections: state.expandedSections.includes(sectionId)
            ? state.expandedSections.filter((s) => s !== sectionId)
            : [...state.expandedSections, sectionId],
        })),
      hydrate: (prefs) =>
        set((state) => ({
          sidebarView: prefs.sidebar_view ?? 'new',
          sidebarFavorites: prefs.sidebar_favorites ?? [],
          // Keep the current language if the backend has none saved yet.
          language: prefs.language ?? state.language,
        })),
      // Note: language is intentionally NOT reset on logout — it stays as a
      // device-level fallback until the next user's saved language hydrates.
      reset: () => set({sidebarView: 'new', sidebarFavorites: [], expandedSections: []}),
    }),
    {name: 'user-preferences'}
  )
)
